// Advertising Contract Manager Component
// Handles creation, signature (OTP), and tracking of advertising contracts
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Plus, FileSignature, Send, CheckCircle, XCircle, Clock, 
  DollarSign, BarChart3, Eye, Download, RefreshCw, AlertTriangle,
  Mail, Phone, Shield, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdContractManager = ({ token }) => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Create contract state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newContract, setNewContract] = useState({
    campaign_name: '',
    total_budget: 100,
    cost_per_click: 0.15,
    start_date: '',
    end_date: '',
    language: 'fr',
    banner_positions: ['top', 'sidebar'],
    target_countries: []
  });
  
  // OTP signature state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [signatureStep, setSignatureStep] = useState(1); // 1: initiate, 2: verify
  const [signatureData, setSignatureData] = useState({
    phone: '',
    sms_code: '',
    email_code: ''
  });
  const [signingLoading, setSigningLoading] = useState(false);
  
  // Stats modal
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [contractStats, setContractStats] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      // Fetch all contracts (admin endpoint)
      const res = await axios.get(`${API_URL}/api/advertising/contracts/all`, { headers });
      setContracts(res.data.contracts || []);
    } catch (error) {
      // Fallback to user's contracts if admin endpoint fails
      try {
        const res = await axios.get(`${API_URL}/api/advertising/contracts/my`, { headers });
        setContracts(res.data.contracts || []);
      } catch (e) {
        toast.error('Erreur lors du chargement des contrats');
      }
    }
    setLoading(false);
  };

  const handleCreateContract = async () => {
    if (!newContract.campaign_name || newContract.total_budget < 50) {
      toast.error('Nom de campagne requis et budget minimum 50€');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/advertising/contracts/create`, newContract, { headers });
      toast.success('Contrat créé avec succès');
      setShowCreateModal(false);
      setNewContract({
        campaign_name: '',
        total_budget: 100,
        cost_per_click: 0.15,
        start_date: '',
        end_date: '',
        language: 'fr',
        banner_positions: ['top', 'sidebar'],
        target_countries: []
      });
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const initiateSignature = async () => {
    if (!signatureData.phone) {
      toast.error('Numéro de téléphone requis pour recevoir le code SMS');
      return;
    }
    
    setSigningLoading(true);
    try {
      await axios.post(`${API_URL}/api/advertising/contracts/initiate-signature`, {
        contract_id: selectedContract.id,
        phone: signatureData.phone
      }, { headers });
      
      toast.success('Codes OTP envoyés par SMS et Email');
      setSignatureStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi des codes');
    }
    setSigningLoading(false);
  };

  const verifySignature = async () => {
    if (!signatureData.sms_code || !signatureData.email_code) {
      toast.error('Les deux codes (SMS et Email) sont requis');
      return;
    }
    
    setSigningLoading(true);
    try {
      await axios.post(`${API_URL}/api/advertising/contracts/verify-signature`, {
        contract_id: selectedContract.id,
        sms_code: signatureData.sms_code,
        email_code: signatureData.email_code
      }, { headers });
      
      toast.success('Contrat signé avec succès !');
      setShowSignatureModal(false);
      setSignatureStep(1);
      setSignatureData({ phone: '', sms_code: '', email_code: '' });
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codes invalides ou expirés');
    }
    setSigningLoading(false);
  };

  const openSignatureModal = (contract) => {
    setSelectedContract(contract);
    setSignatureStep(1);
    setSignatureData({ phone: '', sms_code: '', email_code: '' });
    setShowSignatureModal(true);
  };

  const viewStats = async (contract) => {
    try {
      const res = await axios.get(`${API_URL}/api/advertising/contracts/${contract.id}/stats`, { headers });
      setContractStats(res.data);
      setSelectedContract(contract);
      setShowStatsModal(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-slate-500/20 text-slate-400', label: '📝 Brouillon' },
      pending_signature: { color: 'bg-yellow-500/20 text-yellow-400', label: '✍️ En attente de signature' },
      signed: { color: 'bg-blue-500/20 text-blue-400', label: '✅ Signé' },
      pending_payment: { color: 'bg-orange-500/20 text-orange-400', label: '💳 En attente paiement' },
      active: { color: 'bg-green-500/20 text-green-400', label: '🟢 Actif' },
      paused: { color: 'bg-gray-500/20 text-gray-400', label: '⏸️ En pause' },
      completed: { color: 'bg-purple-500/20 text-purple-400', label: '✓ Terminé' },
      cancelled: { color: 'bg-red-500/20 text-red-400', label: '❌ Annulé' }
    };
    const c = config[status] || config.draft;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const filteredContracts = contracts.filter(c => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return ['draft', 'pending_signature', 'pending_payment'].includes(c.status);
    if (activeTab === 'active') return c.status === 'active';
    if (activeTab === 'completed') return ['completed', 'cancelled'].includes(c.status);
    return true;
  });

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="ad-contract-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileSignature className="w-6 h-6 text-blue-400" />
            Contrats Publicitaires
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Gestion des contrats avec signature OTP (SMS + Email)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchContracts}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Contrat
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-400">{contracts.length}</p>
            <p className="text-xs text-blue-300">Total Contrats</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-400">
              {contracts.filter(c => ['draft', 'pending_signature', 'pending_payment'].includes(c.status)).length}
            </p>
            <p className="text-xs text-yellow-300">En attente</p>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-400">
              {contracts.filter(c => c.status === 'active').length}
            </p>
            <p className="text-xs text-green-300">Actifs</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-400">
              {contracts.reduce((sum, c) => sum + (c.total_budget || 0), 0).toFixed(0)}€
            </p>
            <p className="text-xs text-purple-300">Budget Total</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        {[
          { id: 'all', label: 'Tous', icon: FileSignature },
          { id: 'pending', label: 'En attente', icon: Clock },
          { id: 'active', label: 'Actifs', icon: CheckCircle },
          { id: 'completed', label: 'Terminés', icon: BarChart3 }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === tab.id 
                ? 'bg-slate-700 text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contracts List */}
      <div className="space-y-4">
        {filteredContracts.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            Aucun contrat dans cette catégorie
          </div>
        ) : (
          filteredContracts.map(contract => (
            <Card key={contract.id} className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white font-semibold">{contract.campaign_name}</h3>
                      {getStatusBadge(contract.status)}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-slate-400">
                      <div>
                        <span className="text-slate-500">Annonceur:</span>
                        <p className="text-white">{contract.advertiser_name}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Budget:</span>
                        <p className="text-white font-medium">{contract.total_budget}€</p>
                      </div>
                      <div>
                        <span className="text-slate-500">CPC:</span>
                        <p className="text-white">{contract.cost_per_click}€</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Créé le:</span>
                        <p className="text-white">{new Date(contract.created_at).toLocaleDateString('fr')}</p>
                      </div>
                    </div>
                    {contract.start_date && (
                      <p className="text-xs text-slate-500 mt-2">
                        Période: {contract.start_date} → {contract.end_date || 'Indéfini'}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-4">
                    {(contract.status === 'draft' || contract.status === 'pending_signature') && (
                      <Button 
                        size="sm" 
                        onClick={() => openSignatureModal(contract)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <FileSignature className="w-4 h-4 mr-1" />
                        Signer
                      </Button>
                    )}
                    
                    {contract.status === 'active' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => viewStats(contract)}
                        className="border-blue-500 text-blue-400"
                      >
                        <BarChart3 className="w-4 h-4 mr-1" />
                        Stats
                      </Button>
                    )}
                    
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-slate-600"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Contract Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Nouveau Contrat Publicitaire
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-400">Nom de la campagne *</Label>
              <Input
                value={newContract.campaign_name}
                onChange={(e) => setNewContract({...newContract, campaign_name: e.target.value})}
                placeholder="Ex: Campagne Hiver 2026"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Budget total (€) *</Label>
                <Input
                  type="number"
                  min={50}
                  value={newContract.total_budget}
                  onChange={(e) => setNewContract({...newContract, total_budget: parseFloat(e.target.value)})}
                  className="bg-slate-700 border-slate-600 text-white"
                />
                <p className="text-xs text-slate-500 mt-1">Minimum: 50€</p>
              </div>
              <div>
                <Label className="text-slate-400">Coût par clic (€)</Label>
                <Input
                  type="number"
                  min={0.10}
                  max={0.20}
                  step={0.01}
                  value={newContract.cost_per_click}
                  onChange={(e) => setNewContract({...newContract, cost_per_click: parseFloat(e.target.value)})}
                  className="bg-slate-700 border-slate-600 text-white"
                />
                <p className="text-xs text-slate-500 mt-1">0.10€ - 0.20€</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Date de début</Label>
                <Input
                  type="date"
                  value={newContract.start_date}
                  onChange={(e) => setNewContract({...newContract, start_date: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-400">Date de fin</Label>
                <Input
                  type="date"
                  value={newContract.end_date}
                  onChange={(e) => setNewContract({...newContract, end_date: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-400">Langue du contrat</Label>
              <Select 
                value={newContract.language} 
                onValueChange={(v) => setNewContract({...newContract, language: v})}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="fr" className="text-white">Français</SelectItem>
                  <SelectItem value="en" className="text-white">English</SelectItem>
                  <SelectItem value="de" className="text-white">Deutsch</SelectItem>
                  <SelectItem value="es" className="text-white">Español</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-blue-400 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Note importante
              </p>
              <p className="text-blue-300/70 text-xs mt-1">
                Les publicités sont affichées UNIQUEMENT aux utilisateurs non-premium.
                Les utilisateurs Premium bénéficient d'une expérience sans publicité.
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={() => setShowCreateModal(false)} variant="outline" className="flex-1">
                Annuler
              </Button>
              <Button onClick={handleCreateContract} className="flex-1 bg-blue-600 hover:bg-blue-700">
                Créer le contrat
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* OTP Signature Modal */}
      <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              Signature Électronique Sécurisée
            </DialogTitle>
          </DialogHeader>

          {selectedContract && (
            <div className="space-y-4 mt-4">
              {/* Contract Info */}
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-white font-medium">{selectedContract.campaign_name}</p>
                <p className="text-slate-400 text-sm">Budget: {selectedContract.total_budget}€</p>
              </div>

              {signatureStep === 1 ? (
                <>
                  {/* Step 1: Enter phone and initiate */}
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-400 text-sm font-medium mb-2">
                      Étape 1/2: Envoi des codes OTP
                    </p>
                    <p className="text-yellow-300/70 text-xs">
                      Des codes de vérification seront envoyés par SMS et Email pour sécuriser votre signature.
                    </p>
                  </div>

                  <div>
                    <Label className="text-slate-400 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Numéro de téléphone *
                    </Label>
                    <Input
                      value={signatureData.phone}
                      onChange={(e) => setSignatureData({...signatureData, phone: e.target.value})}
                      placeholder="+33612345678"
                      className="bg-slate-700 border-slate-600 text-white mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Le code SMS sera envoyé à ce numéro
                    </p>
                  </div>

                  <Button 
                    onClick={initiateSignature} 
                    disabled={signingLoading}
                    className="w-full bg-yellow-600 hover:bg-yellow-700"
                  >
                    {signingLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Envoyer les codes OTP
                  </Button>
                </>
              ) : (
                <>
                  {/* Step 2: Verify codes */}
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-400 text-sm font-medium mb-2">
                      Étape 2/2: Vérification des codes
                    </p>
                    <p className="text-green-300/70 text-xs">
                      Entrez les codes reçus par SMS et Email pour finaliser votre signature.
                    </p>
                  </div>

                  <div>
                    <Label className="text-slate-400 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Code SMS (6 chiffres)
                    </Label>
                    <Input
                      value={signatureData.sms_code}
                      onChange={(e) => setSignatureData({...signatureData, sms_code: e.target.value})}
                      placeholder="123456"
                      maxLength={6}
                      className="bg-slate-700 border-slate-600 text-white mt-1 text-center text-2xl tracking-widest"
                    />
                  </div>

                  <div>
                    <Label className="text-slate-400 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Code Email (8 caractères)
                    </Label>
                    <Input
                      value={signatureData.email_code}
                      onChange={(e) => setSignatureData({...signatureData, email_code: e.target.value})}
                      placeholder="ABCD1234"
                      maxLength={8}
                      className="bg-slate-700 border-slate-600 text-white mt-1 text-center text-2xl tracking-widest"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={() => setSignatureStep(1)} 
                      variant="outline"
                      className="flex-1"
                    >
                      Retour
                    </Button>
                    <Button 
                      onClick={verifySignature} 
                      disabled={signingLoading}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      {signingLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Signer le contrat
                    </Button>
                  </div>

                  <Button 
                    onClick={initiateSignature} 
                    variant="ghost"
                    size="sm"
                    className="w-full text-slate-400"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Renvoyer les codes
                  </Button>
                </>
              )}

              <p className="text-xs text-slate-500 text-center">
                🔒 Signature sécurisée conforme eIDAS
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stats Modal */}
      <Dialog open={showStatsModal} onOpenChange={setShowStatsModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              Statistiques de campagne
            </DialogTitle>
          </DialogHeader>

          {contractStats && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-slate-700/50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-white">{contractStats.impressions || 0}</p>
                    <p className="text-sm text-slate-400">Impressions</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-700/50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-400">{contractStats.clicks || 0}</p>
                    <p className="text-sm text-slate-400">Clics</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-700/50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-blue-400">
                      {contractStats.ctr ? contractStats.ctr.toFixed(2) : 0}%
                    </p>
                    <p className="text-sm text-slate-400">CTR</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-700/50">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-purple-400">
                      {contractStats.spent?.toFixed(2) || 0}€
                    </p>
                    <p className="text-sm text-slate-400">Dépensé</p>
                  </CardContent>
                </Card>
              </div>

              <div className="p-3 bg-slate-700/30 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Budget restant:</span>
                  <span className="text-white font-medium">
                    {(contractStats.budget_remaining || 0).toFixed(2)}€
                  </span>
                </div>
                <div className="w-full bg-slate-600 rounded-full h-2 mt-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(100, ((contractStats.spent || 0) / (contractStats.total_budget || 1)) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdContractManager;
